<template>
  <section class="financial__review financial__panel" aria-labelledby="financial-shipment-title">
    <header class="financial__detail-head">
      <button class="financial__back-link" type="button" @click="back">
        <mdicon name="arrow-left" :size="18" aria-hidden="true" /> {{ backLabel }}
      </button>
      <div class="financial__title-line">
        <h1 id="financial-shipment-title" tabindex="-1">{{ shipment.trackingNumbers.join(', ') }}</h1>
        <span class="financial__badge" :class="statusClass">{{ statusLabel }}</span>
      </div>
      <p class="financial__help">
        {{ shipment.billingName }} · booked <span class="financial__nowrap">{{ bookedDate }}</span> ·
        <span class="financial__nowrap">{{ shipment.itemReference }}</span>
      </p>
    </header>

    <!-- Only exceptions: a clean shipment has no summary. -->
    <section
      v-if="attention || missing.length"
      class="financial__review-summary"
      :class="{ 'financial__review-summary--different': attention }"
      aria-labelledby="financial-shipment-summary"
    >
      <mdicon :name="attention ? 'alert-circle-outline' : 'information-outline'" aria-hidden="true" />
      <div>
        <h2 id="financial-shipment-summary">{{ summaryTitle }}</h2>
        <dl v-if="differences.length || missing.length" class="financial__summary-list">
          <div v-if="differences.length"><dt>Differs</dt><dd>{{ differences.join(' · ') }}</dd></div>
          <div v-if="missing.length"><dt>Missing</dt><dd>{{ missing.join(', ') }}</dd></div>
        </dl>
      </div>
    </section>

    <div class="financial__price-grid financial__price-grid--two" role="group" aria-label="Separate shipment price sources">
      <div>
        <span>Quoted customer price</span><strong>{{ amount(shipment.bookedPrice, 'SEK') }}</strong>
        <small>Price offered at booking, before later carrier charges.</small>
      </div>
      <div>
        <span>Actual carrier cost to date</span><strong>{{ entry.lines.length ? amount(entry.cost) : '—' }}</strong>
        <small>All imported charges for this shipment. More costs or credits may arrive.</small>
      </div>
    </div>
    <div class="financial__price-grid financial__price-grid--two" role="group" aria-label="Reviewed customer amounts">
      <div>
        <span>Reviewed customer amount</span><strong>{{ entry.price === null ? '—' : amount(entry.price) }}</strong>
        <small>{{ priceCaption }}</small>
      </div>
      <div>
        <span>Margin</span>
        <strong :class="{ financial__negative: marginNegative }">{{ marginText }}</strong>
        <small v-if="marginPercent" :class="{ financial__negative: marginNegative }">
          <mdicon v-if="marginNegative" name="alert-outline" :size="14" aria-hidden="true" />
          {{ marginPercent }} on cost
        </small>
      </div>
    </div>

    <p v-if="quoteBilling" class="financial__message">
      Quoted booking exported: {{ amount(quoteBilling.amount, 'SEK') }} · {{ quoteBilling.createdAt.slice(0, 10) }} · {{ quoteBilling.userName }}.
      {{ quoteBilling.appliedTo ? 'Reconciled against an approved delivery row; later fees remain separate.' : 'Awaiting reconciliation against an approved delivery row.' }}
    </p>
    <!-- The one next step for this shipment. Rows are approved only in the customer's review. -->
    <div class="financial__next-step">
      <button
        v-if="unlinked"
        class="financial__button financial__button--primary"
        type="button"
        :disabled="busy"
        @click="link"
      >
        Link {{ unlinked }} {{ unlinked === 1 ? 'row' : 'rows' }} to this shipment
      </button>
      <button
        v-if="linkedOpen.length"
        class="financial__button"
        :class="{ 'financial__button--primary': !unlinked }"
        type="button"
        :disabled="busy"
        @click="reviewCustomer"
      >
        Review {{ shipment.billingName }} <mdicon name="arrow-right" :size="18" aria-hidden="true" />
      </button>
      <button
        v-else-if="billable || quoteEligible"
        class="financial__button financial__button--primary"
        type="button"
        :disabled="busy"
        @click="goBilling"
      >
        Bill {{ shipment.billingName }} <mdicon name="arrow-right" :size="18" aria-hidden="true" />
      </button>
      <p v-if="nextStatus">{{ nextStatus }}</p>
    </div>
    <p v-if="failure" class="financial__alert financial__alert--error" role="alert">{{ failure }}</p>
    <p v-else-if="notice" class="financial__message" role="status">{{ notice }}</p>

    <section class="financial__shipment-rows" aria-labelledby="financial-shipment-rows-title">
      <h2 id="financial-shipment-rows-title">{{ rowsTitle }}</h2>
      <div v-if="entry.lines.length" class="financial__table-wrap">
        <table>
          <thead>
            <tr>
              <th scope="col">Date</th>
              <th scope="col">Invoice</th>
              <th scope="col">Charge</th>
              <th scope="col">Actual carrier cost</th>
              <th scope="col">Customer amount</th>
              <th scope="col">Status</th>
              <th scope="col"><span class="visually-hidden">Action</span></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="line in entry.lines" :key="`${line.batch.id}:${line.row.id}`">
              <td class="financial__nowrap">{{ line.row.date || '—' }}</td>
              <td>
                <span class="financial__nowrap">{{ line.batch.carrier }} {{ invoiceLabel(line) }}</span> ·
                row {{ line.row.rowNumber }}<small>{{ sourceText(line) }}</small>
              </td>
              <td>{{ line.row.description }}<small>{{ roleLabel(line) }}</small></td>
              <td class="financial__numeric financial__nowrap">{{ amount(line.amount, line.batch.currency) }}</td>
              <td class="financial__numeric financial__nowrap">
                <template v-if="line.row.review?.status === 'approved'">
                  {{ amount(line.row.review.customerPrice, line.batch.currency) }}<small>{{ approvalText(line) }}</small>
                </template>
                <span v-else-if="isOpen(line)" class="financial__muted">{{ openPrice(line) }}</span>
                <template v-else>—</template>
              </td>
              <td>
                {{ lineStatus(line) }}
                <small v-if="isOpen(line) && blocker(line)" class="financial__attention">{{ blocker(line) }}</small>
              </td>
              <td>
                <button
                  class="financial__button financial__button--small"
                  type="button"
                  :disabled="busy"
                  :aria-label="openLabel(line)"
                  @click="openLine(line)"
                >
                  Open row
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="financial__empty">
        <mdicon name="clock-outline" :size="28" aria-hidden="true" />
        <strong>No carrier cost yet</strong>
        <p>Import the invoice under Carrier invoices when it arrives.</p>
      </div>
    </section>

    <p class="financial__field-hint">Billing applies to individual cost rows. An earlier billing export does not close this shipment to later charges.</p>
    <details class="financial__source">
      <summary>PacSort shipment details</summary>
      <dl class="financial__facts">
        <div><dt>Carrier</dt><dd>{{ shipment.carrier }}</dd></div>
        <div><dt>Service</dt><dd>{{ shipment.service || '—' }}</dd></div>
        <div><dt>Booked weight</dt><dd>{{ shipment.weightGrams === null ? '—' : `${shipment.weightGrams} g` }}</dd></div>
        <div><dt>PacSort reference</dt><dd>{{ shipment.itemReference }}</dd></div>
        <div><dt>Assignment</dt><dd>{{ shipment.assignmentReference || '—' }}</dd></div>
        <div><dt>Customer</dt><dd>{{ shipment.companyName }}</dd></div>
        <div><dt>Sender address</dt><dd>{{ shipment.senderAddressName || 'Not loaded' }}</dd></div>
        <div><dt>Booked dimensions</dt><dd>{{ dimensions }}</dd></div>
        <div><dt>Terminal scan</dt><dd>{{ scannedAt }}</dd></div>
        <div><dt>In this ledger because</dt><dd>{{ shipment.source === 'pacsort' ? 'Loaded from PacSort for a booking period' : 'A carrier invoice row matched it' }}</dd></div>
      </dl>
    </details>
  </section>
</template>
<script lang="ts">
import type { FinancialShipmentDetailStatusClass } from '@/types/Components/FinancialViewState'
import type { FinancialQuoteBilling, FinancialShipmentRecord } from '@/types/Models/Financial'
import type { FinancialLedgerEntry, FinancialPriceSuggestion, FinancialShipmentEntry } from '@/types/Models/Financial'
import type { ComputedRef } from 'vue'
import { computed, defineComponent, type PropType } from 'vue'
import { FINANCIAL_BOOKED_CURRENCY } from '@/const/Financial'
import {
  financialAmountLabel,
  financialCommonCurrency,
  financialDifferenceLabel,
  financialPercent,
  subtractFinancialAmounts,
} from '@/services/FinancialAmounts'
import { financialWeightComparison } from '@/services/FinancialComparison'
import {
  financialInvoiceLabel,
  financialLocalDate,
  financialMissingMetadata,
  financialReady,
} from '@/services/FinancialImport'
import { FINANCIAL_LEDGER_STATUS } from '@/services/FinancialLedger'

export default defineComponent({
  name: 'FinancialShipmentDetail',
  props: {
    quoteBilling: { type: Object as PropType<FinancialQuoteBilling | null>, default: null },
    quoteEligible: { type: Boolean, default: false },
    entry: { type: Object as PropType<FinancialLedgerEntry>, required: true },
    blocker: { type: Function as PropType<(line: FinancialShipmentEntry) => string | null>, required: true },
    // PacSort's price for a row: the booked price for the delivery, carrier cost plus the customer's margin for fees.
    price: { type: Function as PropType<(line: FinancialShipmentEntry) => FinancialPriceSuggestion | null>, required: true },
    busy: { type: Boolean, default: false },
    backLabel: { type: String, default: 'Back to shipments' },
    notice: { type: String, default: '' },
    failure: { type: String, default: '' },
  },
  emits: ['back', 'open-line', 'link', 'go-billing', 'review'],
  setup(props, { emit }) {
    const shipment: ComputedRef<FinancialShipmentRecord> = computed(() => props.entry.shipment)
    const currency: ComputedRef<string> = computed(() => financialCommonCurrency(props.entry.lines.map((line) => line.batch.currency)) ?? '')
    const amount = (value: string | null, unit = currency.value): string => financialAmountLabel(value, unit)
    const statusLabel: ComputedRef<string> = computed(() => FINANCIAL_LEDGER_STATUS[props.entry.status])
    const statusClass: ComputedRef<FinancialShipmentDetailStatusClass> = computed(() => ({
      'financial__badge--ready': ['ready', 'billed'].includes(props.entry.status),
      'financial__badge--review': ['to-review', 'rebill'].includes(props.entry.status),
    }))
    const isOpen = (line: FinancialShipmentEntry): boolean =>
      !financialReady(line.batch, line.row) && line.row.review?.status !== 'excluded'
    const when = (value: string): string =>
      new Date(value).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Europe/Stockholm' })
    // Who approved the customer amount, when, and on which basis.
    const approvalText = (line: FinancialShipmentEntry): string => {
      const review = line.row.review!
      const basis =
        review.priceSource === 'booked'
          ? 'PacSort booked price'
          : review.priceSource === 'margin'
            ? `cost + ${review.marginPercent ?? '?'}% margin`
            : 'entered by reviewer'
      return `Approved ${when(review.reviewedAt)} · ${review.userName} · ${basis}`
    }
    // An open row's price until someone approves it in the customer's review.
    const openPrice = (line: FinancialShipmentEntry): string => {
      const price = props.price(line)
      return price ? `PacSort ${amount(price.amount, price.source === 'booked' ? FINANCIAL_BOOKED_CURRENCY : line.batch.currency)} · ${price.label}` : 'No PacSort price'
    }
    const openLines: ComputedRef<FinancialShipmentEntry[]> = computed(() => props.entry.lines.filter((line) => isOpen(line)))
    const unlinked: ComputedRef<number> = computed(() => props.entry.lines.filter((line) => line.row.matchStatus === 'unmatched').length)
    // Open rows the customer's review can show: an unlinked row is linked to the shipment first.
    const linkedOpen: ComputedRef<FinancialShipmentEntry[]> = computed(() => openLines.value.filter((line) => line.row.matchStatus !== 'unmatched'))
    const approvedRows: ComputedRef<number> = computed(() => props.entry.ready + props.entry.billed + props.entry.changed)
    // A shipment without cost rows, or with every row excluded, says so in its status badge and next step.
    const priceText: ComputedRef<string> = computed(() => {
      if (!openLines.value.length && !approvedRows.value) return ''
      if (props.entry.price === null) return 'An open row needs a price'
      if (!openLines.value.length) return 'Approved'
      return approvedRows.value ? 'Approved rows plus PacSort prices for open rows' : 'PacSort prices · not approved yet'
    })
    const priceCaption: ComputedRef<string> = computed(() => priceText.value)
    const bookedDate: ComputedRef<string> = computed(() => financialLocalDate(shipment.value.bookedAt) || '—')
    // Approved rows not billed yet, or billed rows with a new amount: the next step is the customer's Bill section.
    const billable: ComputedRef<boolean> = computed(
      () => !openLines.value.length && props.entry.lines.some((line) => !line.billed && financialReady(line.batch, line.row)),
    )
    // The state of the next step when there is nothing to click, or why the button is there.
    const nextStatus: ComputedRef<string> = computed(() => {
      if (unlinked.value) return 'The shipment search did not return this shipment when the invoice was matched'
      const open = openLines.value.length
      if (open) return `${open} open ${open === 1 ? 'row' : 'rows'} · approved in the customer's review`
      if (billable.value) return ''
      if (props.entry.status === 'awaiting-cost') return 'The quoted booking can be billed before the carrier invoice arrives'
      if (props.entry.status === 'billed') return 'Billed · new fees appear here'
      if (props.entry.status === 'no-revenue') return 'All costs excluded'
      return statusLabel.value
    })
    // Margin as PacSort defines it: the markup on carrier cost.
    const marginNegative: ComputedRef<boolean> = computed(() => props.entry.margin !== null && Number(props.entry.margin) < 0)
    const marginText: ComputedRef<string> = computed(() =>
      props.entry.margin === null ? '—' : financialDifferenceLabel(props.entry.margin, currency.value),
    )
    const marginPercent: ComputedRef<string | null> = computed(() => {
      const { margin, cost } = props.entry
      return margin === null || cost === null ? null : financialPercent(margin, cost)
    })
    const differences: ComputedRef<string[]> = computed(() => {
      const items: string[] = []
      const { loss, baseCost } = props.entry
      const price = shipment.value.bookedPrice
      if (loss && baseCost !== null && price !== null)
        items.push(`Delivery cost ${financialDifferenceLabel(subtractFinancialAmounts(baseCost, price), currency.value)} above the booked customer price`)
      if (props.entry.deliveries > 1) items.push(`${props.entry.deliveries} delivery rows for one shipment`)
      for (const line of props.entry.lines) {
        if (line.role !== 'base' || !line.row.match) continue
        const weight = financialWeightComparison(line.batch, line.row)
        if (weight.state === 'different') items.push(`Weight ${weight.difference}`)
      }
      const fees = props.entry.lines.filter((line) => line.role === 'additional').length
      if (fees) items.push(`${fees} additional ${fees === 1 ? 'fee' : 'fees'}`)
      return [...new Set(items)]
    })
    // Invoice details the rows still need. A missing carrier cost and unlinked rows are the next step's to say.
    const missing: ComputedRef<string[]> = computed(() => {
      const items: string[] = []
      for (const batch of new Map(props.entry.lines.map((line) => [line.batch.id, line.batch])).values()) {
        const details = financialMissingMetadata(batch)
        if (details.length) items.push(`${details.join(' and ')} on invoice ${financialInvoiceLabel(batch)}`)
      }
      return items
    })
    // A difference or a change after billing needs attention; missing invoice details only need completing.
    const attention: ComputedRef<boolean> = computed(() => differences.value.length > 0 || props.entry.status === 'rebill')
    const summaryTitle: ComputedRef<string> = computed(() => {
      if (props.entry.status === 'rebill') return 'Changed after billing'
      if (props.entry.loss) return 'The delivery cost more than the customer price'
      if (props.entry.deliveries > 1) return 'Several delivery rows for one shipment'
      if (props.entry.weightDiffers) return 'Measured weight differs from the booking'
      if (differences.value.length) return 'Additional fees for this shipment'
      return 'Invoice details needed'
    })
    const scannedAt: ComputedRef<string> = computed(() => {
      const value = shipment.value.receivedInFacilityAt
      return value
        ? new Date(value).toLocaleString('sv-SE', { timeZone: 'Europe/Stockholm', dateStyle: 'short', timeStyle: 'short' })
        : 'Not scanned at the terminal'
    })
    const dimensions: ComputedRef<string> = computed(() => {
      const size = shipment.value.dimensionsCm
      return size ? `${size.length ?? '—'} × ${size.width ?? '—'} × ${size.height ?? '—'} cm` : 'Not booked'
    })
    const invoiceLabel = (line: FinancialShipmentEntry): string => financialInvoiceLabel(line.batch)
    const rowsTitle: ComputedRef<string> = computed(() => {
      const count = props.entry.lines.length
      return count ? `Carrier costs · ${count} ${count === 1 ? 'row' : 'rows'}` : 'Carrier costs'
    })
    // The source of a cost row: the invoice file, and when and by whom it was imported.
    const sourceText = (line: FinancialShipmentEntry): string =>
      `${line.batch.fileName} · imported ${when(line.batch.createdAt)} by ${line.batch.createdByName}`
    const openLabel = (line: FinancialShipmentEntry): string =>
      `Open ${line.row.description}, row ${line.row.rowNumber} on ${line.batch.carrier} invoice ${invoiceLabel(line)}`
    const roleLabel = (line: FinancialShipmentEntry): string =>
      line.role === 'base' ? 'Delivery' : line.role === 'credit' ? 'Credit' : 'Additional charge'
    const lineStatus = (line: FinancialShipmentEntry): string => {
      if (line.changed) return 'Changed after billing'
      if (line.billed) return 'Billed'
      if (financialReady(line.batch, line.row)) return 'Approved'
      if (line.row.review?.status === 'excluded') return 'Excluded'
      if (line.row.matchStatus === 'unmatched') return 'Not linked'
      return 'To approve'
    }
    const back = (): void => {
      emit('back')
    }
    const openLine = (line: FinancialShipmentEntry): void => {
      emit('open-line', line.batch.id, line.row.id)
    }
    const link = (): void => {
      emit('link', props.entry.shipment.itemId)
    }
    const goBilling = (): void => {
      emit('go-billing', shipment.value.billingId)
    }
    const reviewCustomer = (): void => {
      emit('review', shipment.value.billingId)
    }
    return {
      shipment,
      amount,
      statusLabel,
      statusClass,
      isOpen,
      approvalText,
      openPrice,
      priceCaption,
      unlinked,
      linkedOpen,
      bookedDate,
      billable,
      nextStatus,
      goBilling,
      reviewCustomer,
      marginNegative,
      marginText,
      marginPercent,
      differences,
      missing,
      attention,
      summaryTitle,
      dimensions,
      scannedAt,
      invoiceLabel,
      rowsTitle,
      sourceText,
      openLabel,
      roleLabel,
      lineStatus,
      back,
      openLine,
      link,
    }
  },
})
</script>
